import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { hasPermission } from '../utils/permissions';
import './CreateClientReferral.css';

/**
 * Landing page for creating a new client and/or referral.
 * Shown when user selects "New Client / Referral" from the Lists sidebar.
 * Visibility requires clients.create or referrals.create; each card is shown only if the user has the corresponding permission.
 */
function CreateClientReferral() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const canCreateClient = hasPermission('clients.create');
  const canCreateReferral = hasPermission('referrals.create');

  const [showIntroInfo, setShowIntroInfo] = useState(false);
  const [showNewClientInfo, setShowNewClientInfo] = useState(false);
  const [showReReferralInfo, setShowReReferralInfo] = useState(false);

  return (
    <div className="create-client-referral">
      <div className="create-client-referral-header">
        <div className="create-client-referral-title-row">
          <h1 className="create-client-referral-page-title">New Client / Referral</h1>
          <button
            type="button"
            className="create-client-referral-info-icon"
            onClick={() => setShowIntroInfo((v) => !v)}
            aria-expanded={showIntroInfo}
            aria-controls="create-client-referral-intro"
            aria-label={showIntroInfo ? 'Hide explanation' : 'Show explanation'}
          >
            ⓘ
          </button>
        </div>
        {showIntroInfo && (
          <p id="create-client-referral-intro" className="create-client-referral-intro">
            Choose how you want to start. New clients: enter the client record (demographics, EI number, etc.), then use &quot;Save and add referral&quot; on that page to create the client and open the referral form for their first referral. Existing clients returning for a new episode: use Re-Referral to select the client and complete the referral with re-referral details.
          </p>
        )}
      </div>

      <div className="create-client-referral-cards">
        {canCreateClient && (
          <section className="create-client-referral-card" aria-labelledby="new-client-heading">
            <h2 id="new-client-heading" className="create-client-referral-card-title">
              New Client
              <button
                type="button"
                className="create-client-referral-info-icon"
                onClick={() => setShowNewClientInfo((v) => !v)}
                aria-expanded={showNewClientInfo}
                aria-controls="create-client-referral-new-client-desc"
                aria-label={showNewClientInfo ? 'Hide explanation' : 'Show explanation'}
              >
                ⓘ
              </button>
            </h2>
            {showNewClientInfo && (
              <p id="create-client-referral-new-client-desc" className="create-client-referral-card-desc">
                Child is new to the system. They already have an EI number and participant ID from Cornerstone. Enter the client record (basic info, demographics, contacts, etc.), then click &quot;Save and add referral&quot; to create the client and open the form for their first referral.
              </p>
            )}
            <button
              type="button"
              className="create-client-referral-btn create-client-referral-btn-primary"
              onClick={() => navigate(tenantPath('/client/new?from=new-client-referral'))}
            >
              Start New Client
            </button>
          </section>
        )}

        {canCreateReferral && (
          <section className="create-client-referral-card" aria-labelledby="re-referral-heading">
            <h2 id="re-referral-heading" className="create-client-referral-card-title">
              Re-Referral (existing client)
              <button
                type="button"
                className="create-client-referral-info-icon"
                onClick={() => setShowReReferralInfo((v) => !v)}
                aria-expanded={showReReferralInfo}
                aria-controls="create-client-referral-rereferral-desc"
                aria-label={showReReferralInfo ? 'Hide explanation' : 'Show explanation'}
              >
                ⓘ
              </button>
            </h2>
            {showReReferralInfo && (
              <p id="create-client-referral-rereferral-desc" className="create-client-referral-card-desc">
                Client is already in the application. The last referral was closed; you are opening a new referral for a new episode of care. Select the client and fill out the referral; re-referral will be checked and you can enter previous service coordinator.
              </p>
            )}
            <button
              type="button"
              className="create-client-referral-btn create-client-referral-btn-primary"
              onClick={() => navigate(tenantPath('/referral/new?reReferral=true&from=new-client-referral'))}
            >
              Start Re-Referral
            </button>
          </section>
        )}
      </div>
    </div>
  );
}

export default CreateClientReferral;
